import {
  defaultQuoteTypeOptions,
  defaultReleaseCompetitionOptions,
  defaultReleaseCoverOptions,
  defaultReleaseCoverSpreadOptions,
  defaultReleaseDirectionOptions,
} from "@/components/widgets/tradeActionSummary/helper/tradeSettingsHelper/toggleHandler";
import {
  ICPSettingsOverride,
  IToggleButton,
  IToggleButtonToReturnBoolean,
} from "@/components/widgets/tradeActionSummary/helper/tradeSettingsHelper/tradeSettingsInterfaces";

// --- LQC-774: Edit Panel settings hydration (pure helpers) ---
// Response shape mirrors the outgoing new-trade payload built by
// generateOutgoingTradeMessage() in SelectPanelIndex.tsx — same field names,
// same per-CP structure.

export interface IEditPanelSettingsCPResponse {
  counterparty: string;
  quoteType?: string;
  releaseDirection?: boolean;
  autoAffirm?: boolean;
  releaseCompetition?: boolean;
  releaseCover?: boolean;
  releaseCoverSpread?: boolean; // LQC-1705: per-CP Yes/No (was trade-wide string)
  // API can return null when the value is unset on the server. Treat null
  // the same as missing — `?? undefined` at the hydration call site keeps
  // setBoolToggleActiveByValue's undefined-guard active.
  releaseCounterparty?: boolean | null;
  releaseSize?: number; // decimal (0-1) from API; UI stores as percent (LQC-623)
  releaseAllocation?: boolean;
  releaseSecurity?: boolean;
}

export interface IEditPanelSettingsResponse {
  esid: string;
  requestType?: string;
  expiryPeriod?: number;
  expiryPeriodType?: string;
  expiryType?: string;
  minimumSize?: number;
  counterparties: IEditPanelSettingsCPResponse[];
}

// Clone defaults so the returned array is safe to mutate.
// Returns a fresh IToggleButton[] with `active` flipped so that the option
// matching `name` is active (others inactive). If no option matches, all
// options are inactive.
export function setToggleActiveByName(
  defaults: IToggleButton[],
  name: string | undefined,
): IToggleButton[] {
  return defaults.map((opt) => ({ ...opt, active: opt.name === name }));
}

// As above but for boolean-valued toggle options. Matches by `booleanValue`.
// If `value` is undefined, returns a clone of `defaults` untouched.
export function setBoolToggleActiveByValue(
  defaults: IToggleButtonToReturnBoolean[],
  value: boolean | undefined,
): IToggleButtonToReturnBoolean[] {
  if (value === undefined) return defaults.map((o) => ({ ...o }));
  return defaults.map((opt) => ({
    ...opt,
    active: opt.booleanValue === value,
  }));
}

// Deep-ish clone suitable for the baseline snapshot. Toggle arrays are arrays
// of simple objects, so a per-entry shallow clone is enough.
export function cloneOverrides(
  source: Record<string, ICPSettingsOverride>,
): Record<string, ICPSettingsOverride> {
  const out: Record<string, ICPSettingsOverride> = {};
  for (const [cp, fields] of Object.entries(source)) {
    const next: ICPSettingsOverride = {};
    for (const [k, v] of Object.entries(fields) as [
      keyof ICPSettingsOverride,
      any,
    ][]) {
      (next[k] as any) = Array.isArray(v) ? v.map((o) => ({ ...o })) : v;
    }
    out[cp] = next;
  }
  return out;
}

// Build the per-CP override map from the GET /Trade/Execution/Edit/Settings
// response. A CP only gets an entry for a field when its value differs from the
// hardcoded default — that way the CP OVERRIDES grid shows orange/green only on
// genuine divergence from the trade-level defaults row (LQC-774).
//
// UI-percent conversion for releaseSize lives here too: API sends decimal (0-1),
// UI stores percent (LQC-623).
//
// Every CP gets an entry in the returned record (possibly empty) so the CP row
// renders in the OVERRIDES grid and the user can edit it.
export function buildCPOverridesFromResponse(
  cps: IEditPanelSettingsCPResponse[],
  defaultReleaseSizePercent: number,
): Record<string, ICPSettingsOverride> {
  const defaultQuoteType = defaultQuoteTypeOptions.find((o) => o.active)?.name;
  const defaultReleaseDirection = defaultReleaseDirectionOptions.find(
    (o) => o.active,
  )?.booleanValue;
  const defaultReleaseCompetition = defaultReleaseCompetitionOptions.find(
    (o) => o.active,
  )?.booleanValue;
  const defaultReleaseCover = defaultReleaseCoverOptions.find((o) => o.active)
    ?.booleanValue;
  const defaultReleaseCoverSpread = defaultReleaseCoverSpreadOptions.find(
    (o) => o.active,
  )?.booleanValue;

  // Trade-only fields (`autoAffirm`, `releaseCounterparty`) are intentionally
  // NOT considered for per-CP divergence. They display at the trade-level row
  // only — hydrated from `firstCp` in applyEditSettingsResponseToStore — so the
  // per-CP value is always an echo of the trade-level value. Including them here
  // would cause the CP Overrides grid to expand spuriously whenever the
  // persisted trade-level value differs from the hardcoded UI default.
  // (LQC-1705: releaseCoverSpread is now per-CP and IS considered below.)
  const overrides: Record<string, ICPSettingsOverride> = {};
  for (const cp of cps) {
    const cpOverride: ICPSettingsOverride = {};

    if (cp.quoteType !== undefined && cp.quoteType !== defaultQuoteType) {
      cpOverride.quoteType = setToggleActiveByName(
        defaultQuoteTypeOptions,
        cp.quoteType,
      );
    }
    if (
      cp.releaseDirection !== undefined &&
      cp.releaseDirection !== defaultReleaseDirection
    ) {
      cpOverride.releaseDirection = setBoolToggleActiveByValue(
        defaultReleaseDirectionOptions,
        cp.releaseDirection,
      );
    }
    if (
      cp.releaseCompetition !== undefined &&
      cp.releaseCompetition !== defaultReleaseCompetition
    ) {
      cpOverride.releaseCompetition = setBoolToggleActiveByValue(
        defaultReleaseCompetitionOptions,
        cp.releaseCompetition,
      );
    }
    if (
      cp.releaseCover !== undefined &&
      cp.releaseCover !== defaultReleaseCover
    ) {
      cpOverride.releaseCover = setBoolToggleActiveByValue(
        defaultReleaseCoverOptions,
        cp.releaseCover,
      );
    }
    if (
      cp.releaseCoverSpread !== undefined &&
      cp.releaseCoverSpread !== defaultReleaseCoverSpread
    ) {
      cpOverride.releaseCoverSpread = setBoolToggleActiveByValue(
        defaultReleaseCoverSpreadOptions,
        cp.releaseCoverSpread,
      );
    }
    if (cp.releaseSize !== undefined) {
      // Round to whole % — iceberg is conventionally a whole-number percentage
      // (the OP cell renderer rounds for display too) and the API decimal
      // multiplied by 100 introduces FP imprecision (0.55 * 100 = 55.00000…1).
      const valuePct =
        typeof cp.releaseSize === "number"
          ? Math.round(cp.releaseSize * 100)
          : cp.releaseSize;
      if (valuePct !== defaultReleaseSizePercent) {
        cpOverride.releaseSize = valuePct;
      }
    }

    // Only seed an entry for CPs that actually diverge from defaults — CPs
    // whose API values all match defaults stay implicit and surface via the
    // "+ Add CP" affordance, matching the new-trade rendering rule.
    if (Object.keys(cpOverride).length > 0) {
      overrides[cp.counterparty] = cpOverride;
    }
  }
  return overrides;
}
